import { DateTime } from "luxon";

import LocalForagePlugin, { syncState, tags } from "@/plugins/LocalForagePlugin";
import { tagsSyncKey } from "@/types/products/product-definitions";

import { goFetch } from "@/utils/network-utils";
import type { PersistedQueryPayload } from "@tmi/types/api/fetch";

import type { TagApiResponse, Tags } from "@tmi/types/api/content-models";
import type { Tag } from "@tmi/types/entities";

import { consoleLoggerFactory, getDefaultLoggerOptions, LogLevels } from "@/types/console-logger";

const logger = consoleLoggerFactory.create( getDefaultLoggerOptions( LogLevels.DEBUG ) );

export async function refreshTagsFromServer(): Promise< Tag[] > {
    let allTags: Tag[] = [];

    const t0: number = performance.now();

    let page: number = 0;
    const pageSize: number = 100;

    let tagPayload: Tag[] | undefined = [];

    do {
        const response =
            await goFetch< Tags, PersistedQueryPayload >( {
                endpoint: `${ import.meta.env.VITE_CONTENT_REL_PATH }`,
                payload: {
                    id: 'b9f293cd133e29ae59c3df8a3da55082',
                    variables: {
                        skip: page,
                        limit: pageSize
                    }
                }
            } );

        if ( response?.data?.tagCatalog?.tags ) {
            tagPayload = [ ...( response.data.tagCatalog.tags
                    .map( ( t: TagApiResponse ): Tag => ( {
                        id: t.id,
                        publishedAt: t.published_at,
                        name: t.name,
                        slug: t.slug
                    } ) )
            ) ];

            const t1: number = performance.now();
            logger.info( `Fetching & mapping ${ tagPayload.length } tags items took ${ t1 - t0 } ms` );

            allTags = [ ...tagPayload, ...allTags ];
        }

        // update the page #
        page++;
    } while ( (tagPayload?.length ?? 0) >= pageSize );

    return allTags;
}

export async function loadTagMap( tagsMap: Map< string, Tag > ): Promise< boolean > {
    let updated = false;
    try {
        const t0: number = performance.now();

        if ( typeof tags === 'undefined' || typeof syncState === 'undefined' ) {
            // Wait for the driver to settle
            LocalForagePlugin.initialize();
        }

        await syncState.ready();
        // Check for the next refresh interval
        const syncThresholdInMillis = await syncState.getItem< number >( tagsSyncKey );
        if ( null == syncThresholdInMillis || syncThresholdInMillis < DateTime.utc().toMillis() ) {
            // Retrieve the latest collection of tags from the content service
            const tagsReceived: Tag[] = await refreshTagsFromServer();

            if(tagsReceived.length > 0)
            {
                const nextSyncThresholdInMillis = DateTime.utc().plus( { hours: 4 } ).toMillis();
                await syncState.setItem( tagsSyncKey, nextSyncThresholdInMillis );
                updated = true;
                // Store the items recently retrieved
                await tags.ready();
                await Promise.all( tagsReceived.map( t => tags.setItem( t.id, t ) ) );
                const t1: number = performance.now();
                logger.info( `[ sync-worker ] Refreshed ${ tagsMap.size } tags from server in ${ t1 - t0 } ms` );
            }
            else
            {
                const nextSyncThresholdInMillis = DateTime.utc().plus( { seconds: 15 } ).toMillis();
                await syncState.setItem( tagsSyncKey, nextSyncThresholdInMillis );
            }
        }

        // Make sure map is empty
        tagsMap.clear();

        // Load whatever is in the local cache
        await tags.ready();
        // Iterate over all elements within IDB
        await tags.iterate( ( tag: Tag ) => {
            tagsMap.set( tag.id, tag );
            return;
        } );
    } catch ( err: any ) {
        logger.error( '[ tag-sync-coordinator ] Sync failed:', err );
    }

    return updated;
}
